<script setup lang="ts">
import * as _ from 'lodash-es';
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { EventData } from '@repo/core/models';
import { ResourceConfig } from '@repo/core/resources';
import { EventConstants, MessageConstants } from '@repo/core/constants';
import { useDxEventBus, useDxMessage } from '@repo/vue/utils';
import { useAppStore } from '@repo/vue/stores';
import { DxLoader } from '@repo/vue/components';
import { useDxState } from '@/utils';
import { useEventStore, useRootStore } from '@/stores';
import { appMessage } from '@repo/telemetry-config/messages';

// computed
const target = computed(() => window.parent);

// utils
const dxState = useDxState();
const dxEventBus = useDxEventBus();
// track: emit bridge telemetry (outbound/origin-mismatch/protocol filter) internally.
// This is the single receiving instance, so it owns inbound tracking.
const dxMessage = useDxMessage(target, ResourceConfig.webAngularUrl, { track: true });

// stores
const appStore = useAppStore();
const rootStore = useRootStore();
const eventStore = useEventStore();

// state
const { eventData } = storeToRefs(eventStore);
const { isPending } = storeToRefs(appStore);
const { userProfileId, practiceId, clientId, entityId, tabId, financialYearId, supportAccessRequestId } =
  storeToRefs(rootStore);

// data
const lastNavTime = ref<number>(0);

// constants
const MAX_RETRIES = 10;
const RETRY_DELAY = 1000;

// events
const eventBusListener = (event: string, payload: any) => {
  switch (event) {
    case EventConstants.save:
    case EventConstants.reloadClientMenu:
    case EventConstants.financialYearsChanged:
    case EventConstants.selectedYearChanged:
    case EventConstants.accountLinkComplete:
      sendMessage(event, payload);
      break;
  }
};
dxEventBus.on(eventBusListener);

// messages
const onMessageEvent = async (e: MessageEvent) => {
  if (e.origin !== ResourceConfig.webAngularUrl) return;

  // Guard against messages missing an event property (e.g. third-party scripts, PDF.js string payloads)
  if (_.isEmpty(e.data?.event)) return;

  console.info('vue:onMessageEvent', e);

  // data
  const event = e.data.event;
  const page = e.data.page;
  const tabId = e.data.tabId;
  const userProfileId = e.data.userProfileId;
  const financialYearId = e.data.financialYearId;
  const payload = e.data.payload;

  // headers
  const clientId = e.data.headers?.ClientId;
  const entityId = e.data.headers?.EntityId;
  const practiceId = e.data.headers?.PracticeId;
  const supportAccessRequestId = e.data.headers?.SupportAccessRequestId;

  const eventData: EventData & { timestamp?: number } = {
    event: event,
    page: page,
    tabId: tabId,
    payload: payload,
    userProfileId: userProfileId,
    practiceId: practiceId,
    clientId: clientId,
    entityId: entityId,
    financialYearId: financialYearId,
    supportAccessRequestId: supportAccessRequestId
  };

  if (event === MessageConstants.loadPage || event === MessageConstants.loadTab) {
    eventData.timestamp = Date.now();
    lastNavTime.value = eventData.timestamp;
  }

  await processMessage(eventData);
};

// methods
const init = () => {
  appMessage.recordInitSent();
  sendMessage(MessageConstants.init);
};

const processMessage = async (data: EventData & { timestamp?: number }, retryCount: number = 0) => {
  console.info('vue:processMessage', data);
  appMessage.recordInbound(data.event, appMessage.toContext(data as Record<string, unknown>, 'inbound'));

  // check for race conditions
  if (
    (data.event === MessageConstants.loadPage || data.event === MessageConstants.loadTab) &&
    data.timestamp &&
    data.timestamp !== lastNavTime.value
  ) {
    appMessage.recordStaleNav(appMessage.toContext(data as Record<string, unknown>, 'inbound'));
    return;
  }

  // update stores
  eventStore.$patch({
    eventData: data
  });
  // Tenancy must survive incidental shell messages. save/reloadClientMenu/year-change can arrive
  // without the Client/Practice headers (the shell builds them from localStorage, which can be
  // missing the key), and blanking them would strip the tenancy headers from every subsequent API
  // call (/consolidations/exists 500s, trialBalanceEntries/journalEntries 400s). setContext is
  // non-destructive (a missing header never clobbers; supportAccessRequestId stays message-driven
  // so omission ends a support session). Only explicit flows (closeClient -> clearCurrentClient)
  // clear the client.
  // Additionally, apply context only on first delivery: a retry re-runs the handler once init
  // completes, but its `data` is a stale snapshot (captured up to MAX_RETRIES*RETRY_DELAY ago);
  // re-applying it could overwrite fresher context that arrived in between (the retry loop
  // re-running a header-less message is what blanked clientId in production).
  if (retryCount === 0) {
    eventStore.setContext(data);
  }

  // handle events - block non-init events until init completes (isPending becomes false)
  const allowedEvents = [
    MessageConstants.init,
    MessageConstants.login,
    MessageConstants.logout,
    MessageConstants.closeClient
  ];
  if (data.event && !_.includes(allowedEvents, data.event) && isPending.value) {
    if (retryCount >= MAX_RETRIES) {
      appMessage.recordInitMaxRetries(appMessage.toContext(data as Record<string, unknown>, 'inbound'));
      return;
    }
    console.info(`Waiting for init to complete, re-initializing... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
    appMessage.recordInitRetry(retryCount + 1, MAX_RETRIES);
    sendMessage(MessageConstants.init);
    setTimeout(() => processMessage(data, retryCount + 1), RETRY_DELAY);
    return;
  }

  switch (data.event) {
    case MessageConstants.init:
      appMessage.recordInitReceived();
      await dxState.init(data);
      break;
    case MessageConstants.login:
      await dxState.login();
      break;
    case MessageConstants.logout:
      await dxState.logout();
      break;
    case MessageConstants.loadPage:
      if (data.page) {
        await dxState.loadPage(data.page);
      }
      break;
    case MessageConstants.loadTab:
      if (data.page && data.tabId) {
        await dxState.loadTab(data.page, data.tabId);
      }
      break;
    case MessageConstants.removeTab:
      if (data.tabId) {
        await dxState.removeTab(data.tabId);
      }
      break;
    case MessageConstants.tabOff:
      if (data.tabId) {
        await dxState.tabOff(data.tabId);
      }
      break;
    case MessageConstants.financialYearsChanged:
      if (data.financialYearId) {
        await dxState.financialYearsChanged(data.financialYearId);
      }
      break;
    case MessageConstants.selectedYearChanged:
      if (data.financialYearId) {
        await dxState.selectedYearChanged(data.financialYearId);
      }
      break;
    case MessageConstants.closeClient:
      dxState.closeClient();
      break;
    case MessageConstants.confirmTabClose:
      if (data.page && data.tabId) {
        await dxState.confirmTabClose(data.page, data.tabId);
      }
      break;
    case MessageConstants.reloadClientMenu:
      await dxState.reloadClientMenu();
      break;
    case MessageConstants.save:
      dxState.save(data.payload);
      break;
    case MessageConstants.tokenRenewed:
      if (data.payload) {
        await dxState.renewToken(data.payload);
      }
      break;
    default:
      appMessage.recordUnhandled(data.event, appMessage.toContext(data as Record<string, unknown>, 'inbound'));
      break;
  }
};

const sendMessage = (event: string, payload?: any) => {
  const message = { ...eventData.value };
  message.event = event;
  message.payload = payload;
  message.tabId = tabId.value;
  message.userProfileId = userProfileId.value;
  message.practiceId = practiceId.value;
  message.clientId = clientId.value;
  message.entityId = entityId.value;
  message.financialYearId = financialYearId.value;
  message.supportAccessRequestId = supportAccessRequestId.value;
  dxMessage.sendMessage(message);
};

// watches
watch(dxMessage.event, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    onMessageEvent(newValue);
  }
});

watch(isPending, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    init();
  }
});

// hooks
onMounted(() => {
  init();
});

// exposed for unit testing of the inbound-message / retry logic
defineExpose({ processMessage });
</script>

<template>
  <DxLoader v-if="isPending" />
</template>

<style scoped lang="scss" />
